import { isNativeMobileShell } from "../native/mobileBridge";

const BUILD_VERSION = "5252026";
const PRELOAD_STORAGE_PREFIX = `plutonic-unity-preloaded:${BUILD_VERSION}:`;
const UNITY_CACHE_NAME = `plutonic-unity-webgl-${BUILD_VERSION}`;
const UNITY_DATABASE_NAME = `plutonic-unity-webgl-${BUILD_VERSION}`;
const UNITY_DATABASE_VERSION = 1;
const UNITY_ASSET_STORE = "assets";
const UNITY_CHUNK_STORE = "chunks";
const UNITY_CHUNK_SIZE = 4 * 1024 * 1024;
const UNITY_RETRY_DELAYS = [500, 1500];
let serviceWorkerReady;
let persistentStorageRequested;
let unityDatabaseReady;

export function refreshUrl(url) {
  const refreshed = new URL(url);
  refreshed.searchParams.set("unity-preload-refresh", Date.now().toString());
  return refreshed.href;
}

function abortError(signal) {
  return signal?.reason || new DOMException("Unity preload cancelled", "AbortError");
}

function waitForRetry(delay, signal) {
  if (signal?.aborted) return Promise.reject(abortError(signal));
  return new Promise((resolve, reject) => {
    const timeout = setTimeout(() => {
      signal?.removeEventListener("abort", handleAbort);
      resolve();
    }, delay);
    const handleAbort = () => {
      clearTimeout(timeout);
      reject(abortError(signal));
    };
    signal?.addEventListener("abort", handleAbort, { once: true });
  });
}

export async function retryUnityOperation(
  operation,
  signal,
  retryDelays = UNITY_RETRY_DELAYS
) {
  let lastError;
  for (let attempt = 0; attempt <= retryDelays.length; attempt += 1) {
    if (signal?.aborted) throw abortError(signal);
    try {
      return await operation(attempt);
    } catch (error) {
      if (signal?.aborted) throw abortError(signal);
      lastError = error;
      if (attempt === retryDelays.length) throw error;
      await waitForRetry(retryDelays[attempt], signal);
    }
  }
  throw lastError;
}

function shouldRetryResponse(response) {
  return response.status === 408 || response.status === 425 ||
    response.status === 429 || response.status >= 500;
}

async function fetchWithRetry(url, options = {}) {
  return retryUnityOperation(async () => {
    const response = await fetch(url, options);
    if (shouldRetryResponse(response)) {
      throw new Error(`Unity request failed with status ${response.status}.`);
    }
    return response;
  }, options.signal);
}

async function fetchValidationAttempt(url, options = {}) {
  const timeoutController = new AbortController();
  const timeout = setTimeout(
    () => timeoutController.abort(new DOMException("Unity validation timed out", "TimeoutError")),
    10000
  );
  const abortFromCaller = () => timeoutController.abort(options.signal?.reason);
  options.signal?.addEventListener("abort", abortFromCaller, { once: true });
  try {
    const response = await fetch(url, { ...options, signal: timeoutController.signal });
    if (shouldRetryResponse(response)) {
      throw new Error(`Unity validation failed with status ${response.status}.`);
    }
    return response;
  } finally {
    clearTimeout(timeout);
    options.signal?.removeEventListener("abort", abortFromCaller);
  }
}

async function fetchForValidation(url, options = {}) {
  return retryUnityOperation(
    () => fetchValidationAttempt(url, options),
    options.signal
  );
}

export function isDesktopUnityPreloadEligible() {
  if (isNativeMobileShell()) return false;
  const userAgent = navigator.userAgent || "";
  const isMobileUserAgent = /Android|iPhone|iPad|iPod|Mobile/i.test(userAgent);
  const isIPadDesktopMode =
    navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1;
  return !isMobileUserAgent && !isIPadDesktopMode;
}

const PRODUCTION_ROOM_PATHS = {
  beach: "/beach",
  mountain: "/forest",
  park: "/park",
  campsite: "/campsite",
  "winter-holiday": "/winter-holiday",
  "beach-ai-companion": "/demo",
};

const DEVELOPMENT_ROOM_PATHS = {
  beach: "/devenv-plutonictherapy/beach-dev",
  mountain: "/devenv-plutonictherapy/forest-dev",
  park: "/devenv-plutonictherapy/park-dev",
  campsite: "/devenv-plutonictherapy/campsite-dev",
  "winter-holiday": "/devenv-plutonictherapy/winter-holiday-dev",
  "beach-ai-companion": "/devenv-ai-plutonictherapy/beach-ai-dev",
};

export function getDevelopmentUnityOrigin(roomId, location = window.location) {
  const hasSameOriginUnityProxy = [
    "featureenv.plutonichealth.co",
    "mobiledev.plutonichealth.co",
  ].includes(location.hostname);
  if (hasSameOriginUnityProxy) return location.origin;
  return roomId === "beach-ai-companion"
    ? "https://featureenv.plutonichealth.co"
    : "https://devenv.plutonichealth.co";
}

export function getUnityRoomBaseUrl(roomId) {
  if (window.location.hostname === "plutonichealth.co") {
    return new URL(PRODUCTION_ROOM_PATHS[roomId], window.location.origin).href.replace(/\/$/, "");
  }

  const origin = getDevelopmentUnityOrigin(roomId);
  const path = DEVELOPMENT_ROOM_PATHS[roomId];
  return path ? `${origin}${path}` : null;
}

export function prioritizeUnityRooms(rooms, preferredRoomId) {
  const preloadable = rooms.filter((room) => !room.isComingSoon && getUnityRoomBaseUrl(room.id));
  if (!preferredRoomId) return preloadable;
  return [
    ...preloadable.filter((room) => room.id === preferredRoomId),
    ...preloadable.filter((room) => room.id !== preferredRoomId),
  ];
}

export function hasUnityRoomPreloaded(roomId) {
  try {
    return window.localStorage.getItem(`${PRELOAD_STORAGE_PREFIX}${roomId}`) === "true";
  } catch {
    return false;
  }
}

export function markUnityRoomPreloaded(roomId) {
  try {
    window.localStorage.setItem(`${PRELOAD_STORAGE_PREFIX}${roomId}`, "true");
  } catch {
    // The HTTP cache still works when storage is disabled; only the visual
    // completion marker will be unavailable on the next visit.
  }
}

function unmarkUnityRoomPreloaded(roomId) {
  try {
    window.localStorage.removeItem(`${PRELOAD_STORAGE_PREFIX}${roomId}`);
  } catch {
    // Cache validation still works when localStorage is unavailable.
  }
}

export function createInitialUnityPreloadStates(rooms) {
  if (!isDesktopUnityPreloadEligible()) return {};
  return Object.fromEntries(rooms.map((room) => [
    room.id,
    hasUnityRoomPreloaded(room.id)
      ? { status: "checking", progress: 1, blocksEntry: true }
      : { status: "queued", progress: 0, blocksEntry: false },
  ]));
}

async function ensureUnityCacheServiceWorker() {
  if (!isDesktopUnityPreloadEligible()) return null;
  if (!("serviceWorker" in navigator) || !("caches" in window)) return null;
  if (!persistentStorageRequested && navigator.storage?.persist) {
    persistentStorageRequested = navigator.storage.persist().catch(() => false);
  }
  await persistentStorageRequested;
  if (!serviceWorkerReady) {
    serviceWorkerReady = navigator.serviceWorker
      .register("/unity-preload-sw.js", { scope: "/" })
      .then(() => navigator.serviceWorker.ready)
      .catch(() => null);
  }
  await serviceWorkerReady;
  return caches.open(UNITY_CACHE_NAME);
}

function openUnityDatabase() {
  if (!("indexedDB" in window)) return Promise.resolve(null);
  if (!unityDatabaseReady) {
    unityDatabaseReady = new Promise((resolve, reject) => {
      const request = indexedDB.open(UNITY_DATABASE_NAME, UNITY_DATABASE_VERSION);
      request.onupgradeneeded = () => {
        const database = request.result;
        if (!database.objectStoreNames.contains(UNITY_ASSET_STORE)) {
          database.createObjectStore(UNITY_ASSET_STORE, { keyPath: "url" });
        }
        if (!database.objectStoreNames.contains(UNITY_CHUNK_STORE)) {
          const chunks = database.createObjectStore(UNITY_CHUNK_STORE, { keyPath: "key" });
          chunks.createIndex("url", "url", { unique: false });
        }
      };
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
      request.onblocked = () => reject(new Error("Unity cache database is blocked."));
    });
  }
  return unityDatabaseReady;
}

function completeTransaction(transaction) {
  return new Promise((resolve, reject) => {
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error);
    transaction.onabort = () => reject(transaction.error);
  });
}

async function clearIndexedDbAsset(database, url) {
  const transaction = database.transaction(
    [UNITY_ASSET_STORE, UNITY_CHUNK_STORE],
    "readwrite"
  );
  transaction.objectStore(UNITY_ASSET_STORE).delete(url);
  const cursorRequest = transaction
    .objectStore(UNITY_CHUNK_STORE)
    .index("url")
    .openCursor(IDBKeyRange.only(url));
  cursorRequest.onsuccess = () => {
    const cursor = cursorRequest.result;
    if (!cursor) return;
    cursor.delete();
    cursor.continue();
  };
  await completeTransaction(transaction);
}

async function putIndexedDbChunk(database, url, index, data) {
  const transaction = database.transaction(UNITY_CHUNK_STORE, "readwrite");
  transaction.objectStore(UNITY_CHUNK_STORE).put({
    key: `${url}::${index}`,
    url,
    index,
    data,
  });
  await completeTransaction(transaction);
}

async function getIndexedDbAsset(database, url) {
  if (!database) return null;
  return new Promise((resolve, reject) => {
    const request = database.transaction(UNITY_ASSET_STORE).objectStore(UNITY_ASSET_STORE).get(url);
    request.onsuccess = () => resolve(request.result || null);
    request.onerror = () => reject(request.error);
  });
}

async function clearCachedAssets(database, unityCache, urls) {
  await Promise.all(urls.map(async (url) => {
    await clearIndexedDbAsset(database, url).catch(() => {});
    await unityCache.delete(url).catch(() => {});
  }));
}

export function assetValidatorsMatch(storedAsset, response) {
  const storedHeaders = new Headers(storedAsset.headers || []);
  const storedEtag = storedHeaders.get("etag");
  const currentEtag = response.headers.get("etag");
  if (storedEtag && currentEtag) return storedEtag === currentEtag;

  const storedModified = storedHeaders.get("last-modified");
  const currentModified = response.headers.get("last-modified");
  if (storedModified && currentModified) return storedModified === currentModified;

  const currentLength = Number(response.headers.get("content-length"));
  return currentLength > 0 && storedAsset.byteLength === currentLength;
}

function combineChunks(chunks, byteLength) {
  const combined = new Uint8Array(byteLength);
  let offset = 0;
  chunks.forEach((chunk) => {
    combined.set(chunk, offset);
    offset += chunk.byteLength;
  });
  return combined.buffer;
}

async function storeResponseInIndexedDb(database, url, response, onBytes, signal) {
  if (!response.ok) throw new Error(`Preload failed with status ${response.status}.`);
  await clearIndexedDbAsset(database, url);
  let chunkIndex = 0;
  let storedBytes = 0;
  let bufferedBytes = 0;
  let bufferedChunks = [];

  const flush = async () => {
    if (!bufferedBytes) return;
    await putIndexedDbChunk(
      database,
      url,
      chunkIndex,
      combineChunks(bufferedChunks, bufferedBytes)
    );
    chunkIndex += 1;
    storedBytes += bufferedBytes;
    bufferedBytes = 0;
    bufferedChunks = [];
  };

  try {
    if (!response.body?.getReader) {
      const body = new Uint8Array(await response.arrayBuffer());
      onBytes(body.byteLength);
      bufferedChunks.push(body);
      bufferedBytes = body.byteLength;
      await flush();
    } else {
      const reader = response.body.getReader();
      while (true) {
        if (signal.aborted) throw new DOMException("Preload cancelled", "AbortError");
        const { done, value } = await reader.read();
        if (done) break;
        onBytes(value.byteLength);
        bufferedChunks.push(value);
        bufferedBytes += value.byteLength;
        if (bufferedBytes >= UNITY_CHUNK_SIZE) await flush();
      }
      await flush();
    }

    const headers = Array.from(response.headers.entries())
      .filter(([name]) => !["content-encoding", "content-length"].includes(name.toLowerCase()));
    headers.push(["content-length", String(storedBytes)]);
    const transaction = database.transaction(UNITY_ASSET_STORE, "readwrite");
    transaction.objectStore(UNITY_ASSET_STORE).put({
      url,
      status: response.status,
      statusText: response.statusText,
      headers,
      chunkCount: chunkIndex,
      byteLength: storedBytes,
    });
    await completeTransaction(transaction);
  } catch (error) {
    // Navigation intentionally aborts the selection-page preload. Do not
    // start a potentially large IndexedDB delete transaction at that moment:
    // the newly opened Unity iframe needs to read from this same database,
    // and waiting behind cleanup presents as a blank/white room screen.
    // An incomplete asset has no metadata record, so the service worker will
    // ignore its orphaned chunks. The next preload clears them before writing.
    if (!signal.aborted && error?.name !== "AbortError") {
      await clearIndexedDbAsset(database, url).catch(() => {});
    }
    throw error;
  }
}

export function extractBuildAssetUrls(setupSource, baseUrl) {
  if (!setupSource.includes("createUnityInstance")) {
    throw new Error("Unity build manifest was unavailable at the configured URL.");
  }
  const buildPathMatch = setupSource.match(/var\s+buildUrl\s*=\s*["']([^"']+)["']/);
  const buildPath = buildPathMatch?.[1] || "Build";
  const assetNames = ["loaderUrl", "dataUrl", "frameworkUrl", "codeUrl"]
    .map((key) => {
      const expression = new RegExp(`${key}\\s*:\\s*buildUrl\\s*\\+\\s*["']\\/([^"']+)["']|var\\s+${key}\\s*=\\s*buildUrl\\s*\\+\\s*["']\\/([^"']+)["']`);
      const match = setupSource.match(expression);
      return match?.[1] || match?.[2];
    })
    .filter(Boolean);

  if (assetNames.length < 4) {
    throw new Error("Unity build manifest did not contain all required assets.");
  }

  return assetNames.map((name) => new URL(`${buildPath}/${name}`, `${baseUrl}/`).href);
}

export async function isUnityRoomCached(roomId, signal) {
  if (!isDesktopUnityPreloadEligible()) return false;
  const baseUrl = getUnityRoomBaseUrl(roomId);
  if (!baseUrl || new URL(baseUrl).origin !== window.location.origin) return false;

  // A fresh browser has no completed preload to validate. Avoid making it
  // wait through a manifest request and four asset checks before the first
  // real download can start. If storage was cleared but old browser cache
  // entries remain, treating the room as uncached is the safe outcome.
  if (!hasUnityRoomPreloaded(roomId)) return false;

  let unityCache;
  let unityDatabase;
  let storedAssetUrls = [];
  try {
    const setupUrl = `${baseUrl}/TemplateData/UnitySetup.js`;

    // S3/the origin is checked before local storage. The refresh marker is
    // explicitly bypassed by the service worker, so this cannot be satisfied
    // by an old cached UnitySetup.js response.
    const currentSetupResponse = await fetchForValidation(refreshUrl(setupUrl), {
      cache: "no-store",
      signal,
    });
    if (!currentSetupResponse.ok) throw new Error("Unable to validate Unity build manifest.");
    const currentSetupSource = await currentSetupResponse.clone().text();
    const currentAssetUrls = extractBuildAssetUrls(currentSetupSource, baseUrl);

    unityCache = await ensureUnityCacheServiceWorker();
    if (!unityCache) return false;
    unityDatabase = await openUnityDatabase();
    const storedSetup = await unityCache.match(setupUrl);
    if (!storedSetup) {
      await unityCache.put(setupUrl, currentSetupResponse);
      unmarkUnityRoomPreloaded(roomId);
      return false;
    }
    const storedSetupSource = await storedSetup.text();
    storedAssetUrls = extractBuildAssetUrls(storedSetupSource, baseUrl);
    await unityCache.put(setupUrl, currentSetupResponse);

    if (storedSetupSource !== currentSetupSource) {
      await clearCachedAssets(unityDatabase, unityCache, storedAssetUrls);
      unmarkUnityRoomPreloaded(roomId);
      return false;
    }

    const storedAssetsAreCurrent = await Promise.all(currentAssetUrls.map(async (url) => {
      const storedAsset = await getIndexedDbAsset(unityDatabase, url);
      if (!storedAsset) return false;
      const response = await fetchForValidation(refreshUrl(url), {
        method: "HEAD",
        cache: "no-store",
        signal,
      });
      return response.ok && assetValidatorsMatch(storedAsset, response);
    }));
    if (storedAssetsAreCurrent.every(Boolean)) {
      markUnityRoomPreloaded(roomId);
      return true;
    }

    await clearCachedAssets(unityDatabase, unityCache, currentAssetUrls);
    unmarkUnityRoomPreloaded(roomId);

    // A miss in the dedicated cache means this version still needs the normal
    // preload. Do not probe the HTTP cache with only-if-cached: Chrome forwards
    // that request through the service worker and rejects fetch() on a miss.
    return false;
  } catch (error) {
    if (signal?.aborted) throw error;
    unmarkUnityRoomPreloaded(roomId);
    try {
      // Do not wait for service-worker readiness here. A failed origin check
      // must promptly unblock Enter even if registration is itself unhealthy.
      unityCache = unityCache || ("caches" in window
        ? await caches.open(UNITY_CACHE_NAME)
        : null);
      unityDatabase = unityDatabase || await openUnityDatabase();
      const setupUrl = `${baseUrl}/TemplateData/UnitySetup.js`;
      if (!storedAssetUrls.length && unityCache) {
        const storedSetup = await unityCache.match(setupUrl);
        if (storedSetup) {
          storedAssetUrls = extractBuildAssetUrls(await storedSetup.text(), baseUrl);
        }
      }
      if (unityDatabase && unityCache) {
        await clearCachedAssets(unityDatabase, unityCache, storedAssetUrls);
        await unityCache.delete(setupUrl);
      }
    } catch {
      // Validation failures default to an invalid cache. A later preload will
      // replace any storage that could not be cleared here.
    }
    return false;
  }
}

export async function preloadUnityRoom(roomId, onProgress, signal) {
  if (!isDesktopUnityPreloadEligible()) {
    throw new Error("Unity WebGL preloading is disabled on mobile devices.");
  }
  const baseUrl = getUnityRoomBaseUrl(roomId);
  if (!baseUrl) throw new Error(`No Unity WebGL build is configured for ${roomId}.`);

  const unityCache = await ensureUnityCacheServiceWorker();
  if (!unityCache) throw new Error("Persistent Unity caching is not available in this browser.");
  const unityDatabase = await openUnityDatabase();
  if (!unityDatabase) throw new Error("Persistent Unity asset storage is not available in this browser.");
  const setupUrl = `${baseUrl}/TemplateData/UnitySetup.js`;
  const setupResponse = await fetchWithRetry(refreshUrl(setupUrl), {
    cache: "no-store",
    signal,
  });
  if (!setupResponse.ok) throw new Error(`Unable to read the Unity build manifest for ${roomId}.`);
  const setupSource = await setupResponse.clone().text();
  await unityCache.put(setupUrl, setupResponse);
  const assetUrls = extractBuildAssetUrls(setupSource, baseUrl);
  const sizes = await Promise.all(assetUrls.map(async (url) => {
    const response = await fetchWithRetry(refreshUrl(url), {
      method: "HEAD",
      cache: "no-store",
      signal,
    });
    return response.ok ? Number(response.headers.get("content-length")) || 0 : 0;
  }));
  const totalBytes = sizes.reduce((sum, size) => sum + size, 0);
  let completedBytes = 0;

  // Chromium's Cache Storage can reject individual Unity files hundreds of MB
  // in size. Store them as small IndexedDB chunks that the service worker can
  // stream back to Unity without rebuilding the whole file in memory.
  for (let assetIndex = 0; assetIndex < assetUrls.length; assetIndex += 1) {
    const url = assetUrls[assetIndex];
    const completedBeforeAsset = completedBytes;
    let storedBytes = 0;
    await retryUnityOperation(async () => {
      storedBytes = 0;
      if (totalBytes === 0) {
        onProgress(Math.min(assetIndex / assetUrls.length, 0.99));
      }
      const response = await fetch(refreshUrl(url), { cache: "no-store", signal });
      if (shouldRetryResponse(response)) {
        throw new Error(`Unity asset request failed with status ${response.status}.`);
      }
      await storeResponseInIndexedDb(
        unityDatabase,
        url,
        response,
        (bytes) => {
          storedBytes += bytes;
          if (totalBytes > 0) {
            onProgress(Math.min((completedBeforeAsset + storedBytes) / totalBytes, 0.99));
          }
        },
        signal
      );
    }, signal);
    completedBytes += sizes[assetIndex] || storedBytes;
    if (totalBytes === 0) {
      onProgress(Math.min((assetIndex + 1) / assetUrls.length, 0.99));
    }
  }
  onProgress(1);
}
